import { useRef, useState } from 'react'
import { FileUp } from 'lucide-react'
import { Button, Checkbox, Chip, Drawer, EmptyState, toast } from '@/components/ui'
import { useApply, usePreview } from '../api/queries'
import type { ApplyResultItem, PreviewItem } from '../api/types'
import { fmtDate, fmtKg } from '../utils/format'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const KIND_CHIP = {
  new: { variant: 'info', label: 'New PO' },
  revised: { variant: 'warning', label: 'Revised' },
  unchanged: { variant: 'neutral', label: 'No change' },
} as const

/**
 * Upload InSpec PO PDFs → see what each one is (new / revised / no change)
 * and exactly what would change → apply the ones ticked. Applying creates
 * or updates the draft Odoo order; nothing happens before Apply.
 */
export function UploadPosDrawer({ open, onOpenChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const preview = usePreview()
  const apply = useApply()
  const [items, setItems] = useState<PreviewItem[]>([])
  const [selected, setSelected] = useState<Record<number, boolean>>({})
  const [tentative, setTentative] = useState<Record<number, boolean>>({})
  const [results, setResults] = useState<ApplyResultItem[] | null>(null)

  const reset = () => {
    setItems([])
    setSelected({})
    setTentative({})
    setResults(null)
    preview.reset()
  }

  const onFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    setResults(null)
    preview.mutate(Array.from(files), {
      onSuccess: (data) => {
        setItems(data.items)
        const sel: Record<number, boolean> = {}
        const tent: Record<number, boolean> = {}
        for (const it of data.items) {
          if (it.document_id == null) continue
          sel[it.document_id] = it.kind === 'new' || it.kind === 'revised'
          tent[it.document_id] = it.existing?.tentative ?? false
        }
        setSelected(sel)
        setTentative(tent)
      },
      onError: (e) => toast.danger(e instanceof Error ? e.message : 'Upload failed'),
    })
  }

  const chosen = items.filter((it) => it.document_id != null && selected[it.document_id])

  const onApply = () => {
    apply.mutate(
      chosen.map((it) => ({ document_id: it.document_id!, tentative: tentative[it.document_id!] })),
      {
        onSuccess: (data) => {
          setResults(data.items)
          const failed = data.items.filter((r) => r.error).length
          if (failed) toast.warning(`${data.items.length - failed} applied, ${failed} failed`)
          else toast.success(`${data.items.length} PO${data.items.length === 1 ? '' : 's'} applied`)
        },
        onError: (e) => toast.danger(e instanceof Error ? e.message : 'Apply failed'),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
      size="wide"
      surface="form"
      title="Upload InSpec POs"
      meta="PDFs from InSpec for Eclipse Z1 (CHMZO20). Revised POs update the existing order."
      footer={
        results ? (
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={chosen.length === 0}
              loading={apply.isPending}
              onClick={onApply}
            >
              Apply {chosen.length || ''} and update Odoo
            </Button>
          </>
        )
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        data-testid="inspec-pdf-input"
        onChange={(e) => {
          onFiles(e.target.files)
          e.target.value = ''
        }}
      />
      <div
        className="mb-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-default p-6 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          onFiles(e.dataTransfer.files)
        }}
      >
        <FileUp size={20} className="text-tertiary" />
        <div className="text-sm text-secondary">Drop PO PDFs here, or</div>
        <Button
          variant="secondary"
          size="sm"
          loading={preview.isPending}
          onClick={() => inputRef.current?.click()}
        >
          Choose files
        </Button>
      </div>

      {items.length === 0 && !preview.isPending ? (
        <EmptyState
          variant="no-data"
          title="No files yet"
          description="Nothing changes until you click Apply."
        />
      ) : null}

      <ol className="flex flex-col gap-3">
        {items.map((it, i) => {
          const result = results?.find((r) => r.document_id === it.document_id)
          return (
            <li key={`${it.filename}-${i}`} className="rounded-md border border-subtle p-3">
              <div className="flex items-center gap-2">
                {it.document_id != null && !results ? (
                  <Checkbox
                    aria-label={`Apply ${it.parsed?.po_number ?? it.filename}`}
                    checked={!!selected[it.document_id]}
                    onChange={(e) =>
                      setSelected((s) => ({ ...s, [it.document_id!]: e.target.checked }))
                    }
                  />
                ) : null}
                <span className="text-role-data-primary">
                  {it.parsed?.po_number ?? it.filename}
                </span>
                {it.kind ? (
                  <Chip size="xs" variant={KIND_CHIP[it.kind].variant}>
                    {KIND_CHIP[it.kind].label}
                  </Chip>
                ) : null}
                {it.error ? (
                  <Chip size="xs" variant="danger">
                    Can't read
                  </Chip>
                ) : null}
                <span className="ml-auto text-xs text-tertiary">{it.filename}</span>
              </div>

              {it.error ? <p className="mt-2 text-sm text-danger-text">{it.error}</p> : null}

              {it.parsed ? (
                <div className="mt-2 text-sm text-secondary">
                  <div>
                    {fmtKg(Number(it.parsed.qty_kg))} ({Number(it.parsed.qty_lb).toLocaleString()}{' '}
                    lb), PO date {fmtDate(it.parsed.po_date)}, ${it.parsed.price_per_kg}/kg
                  </div>
                  <ul className="mt-1">
                    {it.parsed.releases.map((r) => (
                      <li key={r.seq}>
                        Shipment {r.seq}: {fmtKg(Number(r.qty_kg))} on {fmtDate(r.eta)}
                      </li>
                    ))}
                  </ul>
                  {it.changes && it.changes.length > 0 ? (
                    <ul className="mt-2 list-disc pl-5 text-warning-text">
                      {it.changes.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  ) : null}
                  {it.parsed.warnings.length > 0 ? (
                    <ul className="mt-2 list-disc pl-5 text-danger-text">
                      {it.parsed.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  ) : null}
                  {it.document_id != null && !results ? (
                    <div className="mt-2">
                      <Checkbox
                        label="Subject to change (no customer orders behind it yet)"
                        checked={!!tentative[it.document_id]}
                        onChange={(e) =>
                          setTentative((s) => ({ ...s, [it.document_id!]: e.target.checked }))
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {result ? (
                <p
                  className={`mt-2 text-sm ${result.error ? 'text-danger-text' : 'text-success-text'}`}
                >
                  {result.error
                    ? result.error
                    : `Saved. Odoo: ${result.odoo?.action ?? 'not synced'}${result.odoo?.so ? ` ${result.odoo.so}` : ''}`}
                </p>
              ) : null}
            </li>
          )
        })}
      </ol>
    </Drawer>
  )
}
